import React, { useState } from 'react';
import { register, login, forgotPassword, resetPassword } from '../../lib/api';

/**
 * LoginForm — Standard Sign In / Sign Up / Forgot Password / Reset Password.
 * Admin uses the same Sign In form (server detects admin email).
 * Wallet connect is available after login in the dashboard.
 */

/** Get invisible Turnstile token. Returns empty string if unavailable. */
const getTurnstileToken = () => new Promise((resolve) => {
    // Allow up to 4 seconds — mobile networks (especially iPhone) can be slow
    let resolved = false;
    const done = (val) => { if (!resolved) { resolved = true; resolve(val); } };
    const timeout = setTimeout(() => done(''), 4000);
    const finish = (val) => { clearTimeout(timeout); done(val); };

    // If the Turnstile script hasn't loaded (e.g. iOS content blockers), pass through
    if (typeof window === 'undefined' || !window.turnstile) { finish(''); return; }

    try {
        let c = document.getElementById('cf-turnstile-container');
        if (!c) {
            c = document.createElement('div');
            c.id = 'cf-turnstile-container';
            // Hidden off-screen — purely for invisible challenge
            c.style.cssText = 'position:fixed;bottom:-200px;left:-200px;width:1px;height:1px;opacity:0;pointer-events:none;overflow:hidden';
            document.body.appendChild(c);
        }
        c.innerHTML = '';
        window.turnstile.render(c, {
            sitekey: '0x4AAAAAAC-GGWxfI3QUXL8t',
            action: 'login',
            callback: (t) => finish(t),
            'error-callback': () => finish(''),
            'expired-callback': () => finish(''),
            'before-interactive-callback': () => finish(''), // widget needs interaction — skip on mobile
            size: 'invisible',
            appearance: 'interaction-only',
        });
    } catch (_) {
        // Widget failed to render (common on iOS Safari with ITP enabled)
        finish('');
    }
});

const LoginForm = ({ onEnter }) => {
    const [mode, setMode] = useState('signin');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const inputClass = 'w-full bg-white/[0.03] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-600 outline-none focus:border-blue-500/50 focus:bg-white/[0.05] transition-all font-mono';

    const handleSignUp = async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');
        if (password !== confirmPassword) { setError('Passwords do not match'); return; }
        if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
        setLoading(true);
        try {
            const cfToken = await getTurnstileToken();
            await register(email, password, displayName, cfToken);
            onEnter();
        } catch (err) {
            setError(err.body?.error || err.message || 'Registration failed');
        }
        setLoading(false);
    };

    const handleSignIn = async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');
        setLoading(true);
        try {
            const cfToken = await getTurnstileToken();
            await login(email, password, cfToken);
            onEnter();
        } catch (err) {
            setError(err.body?.error || err.message || 'Login failed');
        }
        setLoading(false);
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');
        if (!email) { setError('Enter your email address'); return; }
        setLoading(true);
        try {
            const res = await forgotPassword(email);
            setSuccess(res.message || 'Check your email for the reset link.');
            // If token is returned (dev mode), pre-fill it for convenience
            if (res.resetToken) {
                setResetToken(res.resetToken);
            }
        } catch (err) {
            setError(err.body?.error || err.message || 'Request failed');
        }
        setLoading(false);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');
        if (password !== confirmPassword) { setError('Passwords do not match'); return; }
        if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
        if (!resetToken) { setError('Reset token is required'); return; }
        setLoading(true);
        try {
            const res = await resetPassword(resetToken, password);
            setSuccess(res.message || 'Password reset! You can now sign in.');
            setTimeout(() => { setMode('signin'); setSuccess(''); setPassword(''); setConfirmPassword(''); setResetToken(''); }, 2000);
        } catch (err) {
            setError(err.body?.error || err.message || 'Reset failed');
        }
        setLoading(false);
    };

    return (
        <section id="login-section" className="relative z-10 flex justify-center px-6 py-16">
            <div className="glass-card rounded-xl p-8 md:p-10 w-full max-w-md animate-in animate-in-d3">

                {/* ═══ SIGN IN ═══ */}
                {mode === 'signin' && (
                    <form onSubmit={handleSignIn} className="space-y-5">
                        <div className="text-center mb-3">
                            <h2 className="text-xl font-black text-white tracking-wide">Sign In</h2>
                            <p className="text-xs text-gray-500 mt-1">Enter your email and password</p>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2">Email</label>
                            <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }}
                                placeholder="you@example.com" className={inputClass} autoComplete="email" required id="signin-email" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2">Password</label>
                            <input type="password" value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }}
                                placeholder="••••••••" className={inputClass} autoComplete="current-password" required id="signin-password" />
                        </div>
                        {error && <p className="text-red-400 text-xs text-center font-mono">{error}</p>}
                        {success && <p className="text-green-400 text-xs text-center font-mono">{success}</p>}
                        <button type="submit" disabled={loading} id="signin-btn"
                            className={`w-full py-3.5 rounded-lg font-black text-sm uppercase tracking-[0.2em] transition-all ${loading
                                ? 'bg-blue-900/40 text-blue-300/50 cursor-wait'
                                : 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white shadow-[0_0_30px_rgba(59,130,246,0.2)]'}`}>
                            {loading ? 'Signing In...' : 'Sign In'}
                        </button>
                        <div className="flex justify-between items-center pt-1">
                            <button type="button" onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}
                                className="text-[11px] text-gray-500 hover:text-blue-400 transition-colors">
                                Forgot Password?
                            </button>
                            <button type="button" onClick={() => { setMode('signup'); setError(''); setPassword(''); setConfirmPassword(''); }}
                                className="text-[11px] text-gray-500 hover:text-blue-400 transition-colors">
                                <span className="font-bold">Sign Up</span>
                            </button>
                        </div>
                    </form>
                )}

                {/* ═══ SIGN UP ═══ */}
                {mode === 'signup' && (
                    <form onSubmit={handleSignUp} className="space-y-4">
                        <div className="text-center mb-3">
                            <h2 className="text-xl font-black text-white tracking-wide">Create Account</h2>
                            <p className="text-xs text-gray-500 mt-1">Enter your details to sign up</p>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2">Display Name</label>
                            <input type="text" value={displayName} onChange={(e) => { setDisplayName(e.target.value); setError(''); }}
                                placeholder="Your name" className={inputClass} required id="signup-name" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2">Email</label>
                            <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }}
                                placeholder="you@example.com" className={inputClass} autoComplete="email" required id="signup-email" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2">Password</label>
                            <input type="password" value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }}
                                placeholder="Min 8 characters" className={inputClass} autoComplete="new-password" required id="signup-password" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2">Confirm Password</label>
                            <input type="password" value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                                placeholder="Re-enter password" className={inputClass} autoComplete="new-password" required id="signup-confirm" />
                        </div>
                        {error && <p className="text-red-400 text-xs text-center font-mono">{error}</p>}
                        {success && <p className="text-green-400 text-xs text-center font-mono">{success}</p>}
                        <button type="submit" disabled={loading} id="signup-btn"
                            className={`w-full py-3.5 rounded-lg font-black text-sm uppercase tracking-[0.2em] transition-all ${loading
                                ? 'bg-green-900/40 text-green-300/50 cursor-wait'
                                : 'bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white shadow-[0_0_30px_rgba(34,197,94,0.2)]'}`}>
                            {loading ? 'Creating...' : 'Create Account'}
                        </button>
                        <div className="text-center pt-1">
                            <button type="button" onClick={() => { setMode('signin'); setError(''); setPassword(''); setConfirmPassword(''); }}
                                className="text-xs text-gray-400 hover:text-white transition-colors">
                                Already have an account? <span className="text-blue-400 font-bold">Sign In</span>
                            </button>
                        </div>
                    </form>
                )}

                {/* ═══ FORGOT PASSWORD ═══ */}
                {mode === 'forgot' && (
                    <form onSubmit={handleForgotPassword} className="space-y-5">
                        <div className="text-center mb-3">
                            <h2 className="text-xl font-black text-white tracking-wide">Forgot Password</h2>
                            <p className="text-xs text-gray-500 mt-1">Enter your email to receive a reset link</p>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2">Email</label>
                            <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); setSuccess(''); }}
                                placeholder="you@example.com" className={inputClass} autoComplete="email" required id="forgot-email" />
                        </div>
                        {error && <p className="text-red-400 text-xs text-center font-mono">{error}</p>}
                        {success && <p className="text-green-400 text-xs text-center font-mono">{success}</p>}
                        <button type="submit" disabled={loading} id="forgot-btn"
                            className={`w-full py-3.5 rounded-lg font-black text-sm uppercase tracking-[0.2em] transition-all ${loading
                                ? 'bg-blue-900/40 text-blue-300/50 cursor-wait'
                                : 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white shadow-[0_0_30px_rgba(59,130,246,0.2)]'}`}>
                            {loading ? 'Sending...' : 'Send Reset Link'}
                        </button>
                        {resetToken && (
                            <button type="button" onClick={() => { setMode('reset'); setError(''); setSuccess(''); setPassword(''); setConfirmPassword(''); }}
                                className="w-full py-2.5 rounded-lg text-xs text-green-400 border border-green-500/20 hover:bg-green-500/10 transition-all uppercase tracking-widest font-bold">
                                Reset Password Now →
                            </button>
                        )}
                        <div className="text-center pt-1">
                            <button type="button" onClick={() => { setMode('signin'); setError(''); setSuccess(''); }}
                                className="text-[11px] text-gray-500 hover:text-blue-400 transition-colors">
                                ← Back to Sign In
                            </button>
                        </div>
                    </form>
                )}

                {/* ═══ RESET PASSWORD ═══ */}
                {mode === 'reset' && (
                    <form onSubmit={handleResetPassword} className="space-y-4">
                        <div className="text-center mb-3">
                            <h2 className="text-xl font-black text-white tracking-wide">Reset Password</h2>
                            <p className="text-xs text-gray-500 mt-1">Enter your new password</p>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2">Reset Token</label>
                            <input type="text" value={resetToken} onChange={(e) => { setResetToken(e.target.value); setError(''); }}
                                placeholder="Paste token from email" className={inputClass} required id="reset-token" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2">New Password</label>
                            <input type="password" value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }}
                                placeholder="Min 8 characters" className={inputClass} autoComplete="new-password" required id="reset-password" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2">Confirm Password</label>
                            <input type="password" value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                                placeholder="Re-enter password" className={inputClass} autoComplete="new-password" required id="reset-confirm" />
                        </div>
                        {error && <p className="text-red-400 text-xs text-center font-mono">{error}</p>}
                        {success && <p className="text-green-400 text-xs text-center font-mono">{success}</p>}
                        <button type="submit" disabled={loading} id="reset-btn"
                            className={`w-full py-3.5 rounded-lg font-black text-sm uppercase tracking-[0.2em] transition-all ${loading
                                ? 'bg-green-900/40 text-green-300/50 cursor-wait'
                                : 'bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white shadow-[0_0_30px_rgba(34,197,94,0.2)]'}`}>
                            {loading ? 'Resetting...' : 'Reset Password'}
                        </button>
                        <div className="text-center pt-1">
                            <button type="button" onClick={() => { setMode('signin'); setError(''); setSuccess(''); }}
                                className="text-[11px] text-gray-500 hover:text-blue-400 transition-colors">
                                ← Back to Sign In
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </section>
    );
};

export default LoginForm;
